import Button from 'components/base/Button';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { defaultBreadcrumbItems } from 'data/commonData';
import { useEffect, useState } from 'react';
import { Col, FloatingLabel, Form, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import userService from 'services/users/userService';

const CreateEditUser = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    role: 'user',
    mobile: '',
    cpf: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isEditing && id) {
      const fetchUser = async () => {
        try {
          setLoading(true);
          const user = await userService.getById(id);
          setFormData({
            name: user.name || '',
            username: user.username || '',
            password: '', // Don't pre-fill password for security
            role: user.role || 'user',
            mobile: user.mobile || '',
            cpf: user.cpf || ''
          });
        } catch (err) {
          console.error('Error fetching user:', err);
          setError('Failed to load user data');
        } finally {
          setLoading(false);
        }
      };
      fetchUser();
    }
  }, [id, isEditing]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      if (isEditing && id) {
        // For editing, only include password if it's been changed (not empty)
        const updateData: Partial<typeof formData> = {
          name: formData.name,
          username: formData.username,
          role: formData.role,
          mobile: formData.mobile,
          cpf: formData.cpf
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await userService.update(id, updateData);
      } else {
        // For creating, password is required
        await userService.create(formData);
      }

      // Navigate back to users list
      navigate('/app/users/lists');
    } catch (err) {
      console.error('Error saving user:', err);
      setError('Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/app/users/lists');
  };

  if (loading && isEditing) {
    return (
      <div>
        <PageBreadcrumb items={defaultBreadcrumbItems} />
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: '400px' }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageBreadcrumb items={defaultBreadcrumbItems} />
      <h2 className="mb-4">{isEditing ? 'Editar Usuário' : 'Criar Usuário'}</h2>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <Row>
        <Col xs={12} xl={9}>
          <Row as="form" className="g-3 mb-6" onSubmit={handleSubmit}>
            <Col sm={6} md={8}>
              <FloatingLabel controlId="floatingInputName" label="Nome">
                <Form.Control
                  type="text"
                  placeholder="Nome"
                  value={formData.name}
                  onChange={e => handleChange('name', e.target.value)}
                  required
                />
              </FloatingLabel>
            </Col>
            <Col sm={6} md={4}>
              <FloatingLabel controlId="floatingSelectRole" label="Função">
                <Form.Select
                  value={formData.role}
                  onChange={e => handleChange('role', e.target.value)}
                >
                  <option value="user">Usuário</option>
                  <option value="admin">Administrador</option>
                </Form.Select>
              </FloatingLabel>
            </Col>
            <Col sm={6} md={6}>
              <FloatingLabel
                controlId="floatingInputUsername"
                label="Username/Email"
              >
                <Form.Control
                  type="email"
                  placeholder="Username/Email"
                  value={formData.username}
                  onChange={e => handleChange('username', e.target.value)}
                  required
                />
              </FloatingLabel>
            </Col>
            <Col sm={6} md={6}>
              <FloatingLabel controlId="floatingInputPassword" label="Senha">
                <Form.Control
                  type="password"
                  placeholder="Senha"
                  value={formData.password}
                  onChange={e => handleChange('password', e.target.value)}
                  required={!isEditing} // Only required for new users
                />
              </FloatingLabel>
              {isEditing && (
                <small className="text-muted">
                  Deixe em branco para manter a senha atual
                </small>
              )}
            </Col>
            <Col sm={6} md={6}>
              <FloatingLabel controlId="floatingInputMobile" label="Celular">
                <Form.Control
                  type="text"
                  placeholder="Celular"
                  value={formData.mobile}
                  onChange={e => handleChange('mobile', e.target.value)}
                  required
                />
              </FloatingLabel>
            </Col>
            <Col sm={6} md={6}>
              <FloatingLabel controlId="floatingInputCpf" label="CPF">
                <Form.Control
                  type="text"
                  placeholder="CPF"
                  value={formData.cpf}
                  onChange={e => handleChange('cpf', e.target.value)}
                  required
                />
              </FloatingLabel>
            </Col>
            <Col xs={12} className="gy-6">
              <div className="d-flex justify-content-end gap-3">
                <Button
                  variant="phoenix-primary"
                  className="px-5"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  className="px-5 px-sm-15"
                  type="submit"
                  disabled={loading}
                >
                  {loading
                    ? 'Salvando...'
                    : isEditing
                    ? 'Atualizar Usuário'
                    : 'Criar Usuário'}
                </Button>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default CreateEditUser;
